/**
 * Hook Unificado de Anexos de Transações
 * 
 * Centraliza toda a lógica de gerenciamento de anexos com:
 * - Upload/download de arquivos
 * - Validação de tipos e tamanhos
 * - Operações CRUD de anexos
 * - Interface unificada para componentes
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { TransactionAttachment, AttachmentForm, AttachmentType } from '@/types/transaction';

// Gera um bigint-like numérico (timestamp + aleatório) para atender colunas bigint sem default
const generateNumericId = () => {
  const now = BigInt(Date.now()); // ms
  const rand = BigInt(Math.floor(Math.random() * 1000)); // 0-999
  return (now * 1000n + rand).toString(); // string numérica compatível com bigint
};

export interface AttachmentFile {
  file: File;
  description: string;
  attachment_form?: AttachmentForm;
  attachment_type?: AttachmentType;
}

export interface UseTransactionAttachmentsOptions {
  transactionId?: string;
  maxFileSize?: number; // em bytes
  allowedTypes?: string[];
}

export interface UseTransactionAttachmentsReturn {
  // Estado
  attachments: TransactionAttachment[];
  loading: boolean;
  error: string | null;
  
  // Upload
  uploadAttachments: (userId: string, transactionId: string, files: AttachmentFile[]) => Promise<TransactionAttachment[]>;
  
  // Download
  getAttachmentUrl: (attachment: TransactionAttachment) => Promise<string | null>;
  downloadAttachment: (attachment: TransactionAttachment) => Promise<void>;
  
  // CRUD
  updateAttachment: (id: number, updates: Partial<TransactionAttachment>) => Promise<void>;
  deleteAttachment: (id: number) => Promise<void>;
  
  // Validação
  validateFile: (file: File) => { valid: boolean; error?: string };
  
  // Refresh
  refreshAttachments: () => Promise<void>;
}

const DEFAULT_OPTIONS: Required<UseTransactionAttachmentsOptions> = {
  transactionId: '',
  maxFileSize: 10 * 1024 * 1024, // 10MB
  allowedTypes: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ]
};

export function useTransactionAttachments(
  options: UseTransactionAttachmentsOptions = {}
): UseTransactionAttachmentsReturn {
  const [attachments, setAttachments] = useState<TransactionAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const config = { ...DEFAULT_OPTIONS, ...options };

  /**
   * Busca anexos da transação
   */
  const refreshAttachments = useCallback(async () => {
    if (!config.transactionId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('transaction_attachments')
        .select('*')
        .eq('transaction_id', config.transactionId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setAttachments((data || []).map(att => ({
        ...att,
        attachment_form: att.attachment_form as AttachmentForm,
        attachment_type: att.attachment_type as AttachmentType
      })));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar anexos';
      setError(errorMessage);
      console.error('Error fetching attachments:', err);
    } finally {
      setLoading(false);
    }
  }, [config.transactionId]);

  /**
   * Valida arquivo antes do upload
   */
  const validateFile = useCallback((file: File): { valid: boolean; error?: string } => {
    // Verificar tamanho
    if (file.size > config.maxFileSize) {
      return {
        valid: false,
        error: `Arquivo muito grande. Tamanho máximo: ${formatFileSize(config.maxFileSize)}`
      };
    }

    // Verificar tipo
    if (!config.allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Tipo de arquivo não permitido: ${file.type}`
      };
    }

    return { valid: true };
  }, [config.maxFileSize, config.allowedTypes]);

  /**
   * Faz upload de múltiplos arquivos
   */
  const uploadAttachments = useCallback(async (
    userId: string,
    transactionId: string,
    files: AttachmentFile[]
  ): Promise<TransactionAttachment[]> => {
    const effectiveTransactionId = transactionId || config.transactionId;
    if (!effectiveTransactionId) {
      throw new Error('ID da transação é obrigatório');
    }

    setLoading(true);
    setError(null);

    const uploadedAttachments: TransactionAttachment[] = [];

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('Usuário não autenticado');

      for (const attachmentFile of files) {
        // Validar arquivo
        const validation = validateFile(attachmentFile.file);
        if (!validation.valid) {
          throw new Error(validation.error);
        }

        // Gerar caminho único
        const filePath = `${user.id}/${effectiveTransactionId}/${Date.now()}_${attachmentFile.file.name}`;

        // Upload para storage
        const { error: uploadError } = await supabase.storage
          .from('transaction-attachments')
          .upload(filePath, attachmentFile.file);

        if (uploadError) {
          throw new Error(`Erro ao fazer upload ${attachmentFile.file.name}: ${uploadError.message}`);
        }

        // Registrar no banco
        const insertData: any = {
          id: generateNumericId(),
          transaction_id: effectiveTransactionId,
          file_path: filePath,
          file_name: attachmentFile.file.name,
          file_type: attachmentFile.file.type,
          description: attachmentFile.description,
          user_id: user.id,
          attachment_form: attachmentFile.attachment_form || 'digital',
          attachment_type: attachmentFile.attachment_type || 'other'
        };

        const { data: attachment, error: dbError } = await supabase
          .from('transaction_attachments')
          .insert(insertData)
          .select()
          .single();

        if (dbError) {
          // Rollback: remover arquivo do storage
          await supabase.storage
            .from('transaction-attachments')
            .remove([filePath]);
          
          throw new Error(`Erro ao registrar anexo ${attachmentFile.file.name}: ${dbError.message}`);
        }

        uploadedAttachments.push({
          ...attachment,
          attachment_form: attachment.attachment_form as AttachmentForm,
          attachment_type: attachment.attachment_type as AttachmentType
        });
      }

      // Atualizar estado local
      setAttachments(prev => [...uploadedAttachments.map(att => ({
        ...att,
        attachment_form: att.attachment_form as AttachmentForm,
        attachment_type: att.attachment_type as AttachmentType
      })), ...prev]);

      return uploadedAttachments;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer upload dos anexos';
      setError(errorMessage);
      console.error('Error uploading attachments:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [config.transactionId, validateFile]);

  /**
   * Gera URL pública para download do anexo
   */
  const getAttachmentUrl = useCallback(async (
    attachment: TransactionAttachment
  ): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('transaction-attachments')
        .createSignedUrl(attachment.file_path, 3600); // 1 hora de validade

      if (error) throw error;

      return data.signedUrl;
    } catch (err) {
      console.error('Error getting attachment URL:', err);
      return null;
    }
  }, []);

  /**
   * Faz download do anexo
   */
  const downloadAttachment = useCallback(async (
    attachment: TransactionAttachment
  ): Promise<void> => {
    const url = await getAttachmentUrl(attachment);
    if (!url) {
      throw new Error('Não foi possível gerar URL de download');
    }

    // Criar link temporário e disparar download
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [getAttachmentUrl]);

  /**
   * Atualiza metadados do anexo
   */
  const updateAttachment = useCallback(async (
    id: number,
    updates: Partial<TransactionAttachment>
  ): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('transaction_attachments')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      // Atualizar estado local
      setAttachments(prev =>
        prev.map(att => att.id === id ? { ...att, ...updates } : att)
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar anexo';
      setError(errorMessage);
      console.error('Error updating attachment:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Exclui anexo
   */
  const deleteAttachment = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Buscar dados do anexo antes de excluir
      const attachment = attachments.find(att => att.id === id);
      if (!attachment) {
        throw new Error('Anexo não encontrado');
      }

      // Excluir do banco
      const { error: dbError } = await supabase
        .from('transaction_attachments')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;

      // Excluir do storage
      const { error: storageError } = await supabase.storage
        .from('transaction-attachments')
        .remove([attachment.file_path]);

      if (storageError) {
        console.warn('Erro ao excluir arquivo do storage:', storageError);
      }

      // Atualizar estado local
      setAttachments(prev => prev.filter(att => att.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao excluir anexo';
      setError(errorMessage);
      console.error('Error deleting attachment:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [attachments]);

  return {
    // Estado
    attachments,
    loading,
    error,
    
    // Operações
    uploadAttachments,
    getAttachmentUrl,
    downloadAttachment,
    updateAttachment,
    deleteAttachment,
    validateFile,
    refreshAttachments
  };
}

/**
 * Formata tamanho do arquivo para exibição
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
